import { BattleState, Monster, TurnCommands, TurnResult } from '../types';
import { AIConfig } from './types';
import { selectLv6Core } from './lv6Core';
import { mulberry32, generateSeed } from './random';
import {
  Lv6RequestMessage,
  Lv6WorkerMessage,
  Lv6TimeoutError,
  Lv6WorkerError,
} from './lv6Protocol';
import { getOrCreateAIWorker, WorkerLike } from '../workers/workerFactory';

/**
 * Lv6 達人 AI のメインスレッド側エントリ
 *
 * Worker が利用可能な環境では Worker 経由で計算、利用不可な環境（テスト・古い WebView）
 * ではインライン同期実行にフォールバックする。
 *
 * いずれの場合も Promise<TurnCommands> を返す（API は常に async）。
 */

export interface Lv6AsyncOptions {
  /** 乱数 seed（省略時は generateSeed() で自動生成） */
  randomSeed?: number;
  /** タイムアウト（ms）。既定 1500 */
  timeoutMs?: number;
  /** Worker を使わずインライン実行を強制する（テスト用） */
  inline?: boolean;
  /**
   * ボス専用モディファイア（issue #264 ボス実装で活用）。
   * 通常 Lv6 使用時は省略。指定された場合、自分の候補ペアスコアに
   * このマップを乗算して、ボス特有の好み（例: 禁忌技優先）を反映する。
   *
   * 注: Worker 経由で渡される場合、シリアライズ可能である必要がある
   * （プレーンオブジェクトのみ）。
   */
  bossSpecificMods?: import('./weightedSelection').CommandWeightMap;
}

const DEFAULT_TIMEOUT_MS = 1500;

let requestCounter = 0;
function nextRequestId(): string {
  requestCounter = (requestCounter + 1) % 0x100000000;
  return `lv6-${Date.now().toString(36)}-${requestCounter.toString(36)}`;
}

/**
 * Lv6 達人 AI でコマンドを選択する（async）
 */
export async function selectLv6Async(
  state: BattleState,
  playerId: 'player1' | 'player2',
  monster: Monster,
  opponentMonster: Monster,
  turnHistory: TurnResult[],
  aiConfig: AIConfig,
  options: Lv6AsyncOptions = {},
): Promise<TurnCommands> {
  const seed = options.randomSeed ?? generateSeed();

  // インライン実行モード（テスト時のデフォルト、または明示指定時）
  if (options.inline || typeof Worker === 'undefined') {
    return selectLv6Core(
      state,
      playerId,
      monster,
      opponentMonster,
      turnHistory,
      aiConfig,
      mulberry32(seed),
      options.bossSpecificMods,
    );
  }

  const worker = getOrCreateAIWorker();
  if (!worker) {
    // Worker 生成失敗 → インライン実行にフォールバック
    return selectLv6Core(
      state,
      playerId,
      monster,
      opponentMonster,
      turnHistory,
      aiConfig,
      mulberry32(seed),
      options.bossSpecificMods,
    );
  }

  return runViaWorker(
    worker,
    {
      state,
      playerId,
      monster,
      opponentMonster,
      turnHistory,
      aiConfig,
      randomSeed: seed,
      bossSpecificMods: options.bossSpecificMods,
    },
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
}

/**
 * Worker 経由で Lv6 を実行する
 */
function runViaWorker(
  worker: WorkerLike,
  payload: Lv6RequestMessage['payload'],
  timeoutMs: number,
): Promise<TurnCommands> {
  const requestId = nextRequestId();

  return new Promise<TurnCommands>((resolve, reject) => {
    let settled = false;

    const onMessage = (event: MessageEvent | ErrorEvent) => {
      if (!('data' in event)) return;
      const message = (event as MessageEvent).data as Lv6WorkerMessage;
      if (!message || typeof message !== 'object') return;
      if (!('requestId' in message) || message.requestId !== requestId) return;

      cleanup();
      if (message.kind === 'lv6/response') {
        resolve(message.result);
      } else if (message.kind === 'lv6/error') {
        reject(new Lv6WorkerError(
          message.error.message,
          message.error.name,
          message.error.stack,
        ));
      }
    };

    const onError = (event: MessageEvent | ErrorEvent) => {
      cleanup();
      const errEvent = event as ErrorEvent;
      reject(new Lv6WorkerError(
        errEvent.message ?? 'Worker error',
        errEvent.error?.name ?? 'WorkerError',
        errEvent.error?.stack,
      ));
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Lv6TimeoutError(`Lv6 AI thinking timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    function cleanup() {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.removeEventListener('messageerror', onError);
    }

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    worker.addEventListener('messageerror', onError);

    const request: Lv6RequestMessage = {
      kind: 'lv6/request',
      requestId,
      payload,
    };
    worker.postMessage(request);
  });
}
